/**
 * Settings → Audit: what the audit trail keeps of where and how each thing was done (the address, an approximate
 * place, the app or browser) and for how long. Administrators only; the API refuses anyone else. Calm, as settings
 * are: one group of rows, the three choices, the days, a line showing how an entry will read, and one Save in the last row.
 */

import {
  AUDIT_IP_WORDS,
  AUDIT_LOCATION_WORDS,
  AUDIT_ORIGIN_KEEP_DAYS_MAX,
  AUDIT_USER_AGENT_WORDS,
  auditOriginWords,
  type AuditIpLevel,
  type AuditLocationLevel,
  type AuditOrigin,
  type AuditPrivacy,
  type AuditUserAgentLevel,
} from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, selectClass, StatusMark } from './ui.tsx';

/** How one entry reads under some settings: a made-up request from Sydney in Chrome. */
function preview(s: AuditPrivacy): string {
  const o: AuditOrigin = {
    ...(s.ip === 'full' ? { ip: '203.0.113.42' } : s.ip === 'shortened' ? { ip: '203.0.113.0/24' } : {}),
    ...(s.location === 'off'
      ? {}
      : { location: { source: 'lookup' as const, country: 'AU', ...(s.location !== 'country' ? { region: 'New South Wales' } : {}), ...(s.location === 'city' ? { city: 'Sydney' } : {}) } }),
    ...(s.userAgent === 'off' ? {} : { client: { kind: 'browser' as const, summary: 'Chrome 152 on Windows' } }),
  };
  const words = auditOriginWords(o);
  const address = o.ip && o.location ? ` (address ${o.ip})` : '';
  return words ? `${words}${address}` : 'Nothing about where or how.';
}

const day = (iso: string) => dateMedium(iso);

export function AuditSettingsPanel() {
  const view = useLoad(() => routes.auditSettings(), []);
  const [draft, setDraft] = useState<AuditPrivacy>();
  const [days, setDays] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const v = view.data;
  if (!v) return <ErrorNote error={view.error} />;
  const d = draft ?? v.settings;
  const keepText = days ?? String(d.keepDays);
  const keepDays = Number(keepText);
  const keepValid = keepText.trim() !== '' && Number.isInteger(keepDays) && keepDays >= 0 && keepDays <= AUDIT_ORIGIN_KEEP_DAYS_MAX;
  const changed = Boolean(draft) || (days !== undefined && keepDays !== v.settings.keepDays);
  const edit = (patch: Partial<AuditPrivacy>) => {
    setSaved(false);
    setDraft({ ...d, ...patch });
  };
  const save = async (next: AuditPrivacy) => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.saveAuditSettings(next);
      setDraft(undefined);
      setDays(undefined);
      setSaved(true);
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const isDefault = (['ip', 'location', 'userAgent', 'keepDays'] as const).every((k) => v.settings[k] === v.defaults[k]);
  return (
    <ListGroup
      id="audit-where"
      title="Where and how"
      description="Each entry can record where it came from and what was used: the network address, an approximate place worked out on this server from that address, and the app, browser or script. Keep only what you need, for as long as you need it."
    >
      <ListRow title="IP address" control={<ChoiceSelect<AuditIpLevel> label="IP address" className="w-56" value={d.ip} options={AUDIT_IP_WORDS} onChange={(ip) => edit({ ip })} />} />
      <ListRow title="Place" control={<ChoiceSelect<AuditLocationLevel> label="Place" className="w-56" value={d.location} options={AUDIT_LOCATION_WORDS} onChange={(location) => edit({ location })} />} />
      <ListRow title="App or browser" control={<ChoiceSelect<AuditUserAgentLevel> label="App or browser" className="w-56" value={d.userAgent} options={AUDIT_USER_AGENT_WORDS} onChange={(userAgent) => edit({ userAgent })} />} />
      <ListRow
        title="Keep these details for"
        htmlFor="aioe-audit-keep-days"
        description={keepValid ? (keepDays === 0 ? 'As long as the entry itself is kept.' : 'Then they are cleared each night; the entry itself stays.') : `A whole number of days, 0 to ${AUDIT_ORIGIN_KEEP_DAYS_MAX}.`}
        control={
          <>
            <input
              id="aioe-audit-keep-days"
              className={`${selectClass} w-24 tabular-nums`}
              type="number"
              min={0}
              max={AUDIT_ORIGIN_KEEP_DAYS_MAX}
              step={1}
              value={keepText}
              aria-invalid={!keepValid}
              onChange={(e) => {
                setSaved(false);
                setDays(e.target.value);
              }}
            />
            <span className="text-muted">days</span>
          </>
        }
      />
      <ListRow
        title="An entry will read"
        description={
          <>
            <span className="block text-sm text-foreground">{preview({ ...d, keepDays })}</span>
            {d.userAgent === 'off' ? 'A workbench’s or node’s app version is still kept: it names the software, not the person.' : 'A workbench or node is named with its app version: “AI Workbench 0.89.19 on Windows”.'}
          </>
        }
      />
      <ListBlock className="text-xs text-muted">
        It helps you notice a sign-in or a change from somewhere unexpected, and look into an incident afterwards. Under the Australian Privacy Act an IP address can be personal information. Addresses are never sent anywhere to be looked up, and these details sit beside each entry, outside its tamper-evident chain, so clearing them later changes nothing else.
      </ListBlock>
      <ListRow
        title={changed ? 'Unsaved changes' : 'Saved settings'}
        description={
          <>
            {v.places.available ? (
              <>
                Places are approximate,{' '}
                <a href="https://db-ip.com" target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  IP Geolocation by DB-IP
                </a>
                {v.places.builtAt ? `, updated ${day(v.places.builtAt)}` : ''}.
              </>
            ) : (
              'This deployment has no place database installed, so only private and mesh addresses are named.'
            )}{' '}
            Keeping less applies to what is already kept as well.{v.updatedAt ? ` Last changed ${day(v.updatedAt)}.` : ''}
          </>
        }
        status={saved ? <StatusMark state="connected" label="Saved; recorded in the audit trail" wrap /> : undefined}
        control={
          <>
            {!isDefault && !changed ? (
              <Button size="sm" variant="ghost" isDisabled={busy} onPress={() => void save(v.defaults)}>
                Use the defaults
              </Button>
            ) : null}
            <Button size="sm" variant="primary" isPending={busy} isDisabled={!changed || !keepValid} onPress={() => void save({ ...d, keepDays })}>
              Save
            </Button>
          </>
        }
      />
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
